import algoliasearch from "algoliasearch";
import { Contractor } from "../models/Contractor.interface";

interface SearchFilters {
  rating?: string;
  category?: string;
  location?: string;
}

interface SearchParams {
  searchQuery: string;
  filters: SearchFilters;
  page: number;
  itemsPerPage: number;
}

interface SearchResult {
  contractors: Contractor[];
  totalCount: number;
}

const client = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID || "",
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY || ""
);

const contractorsIndex = client.initIndex("all");

export const useContractorSearch = () => {
  const searchContractors = async ({
    searchQuery,
    filters,
    page,
    itemsPerPage,
  }: SearchParams): Promise<SearchResult> => {
    try {
      const filterArray: string[] = [];

      // Category filter
      if (filters.category?.trim()) {
        filterArray.push(`categories:"${filters.category.trim()}"`);
      }

      // Location filter
      if (filters.location?.trim()) {
        const [city, state] = filters.location
          .split(",")
          .map((part) => part.trim());

        if (city) {
          filterArray.push(`address.city:"${city}"`);
          if (state) {
            filterArray.push(`address.state:"${state}"`);
          }
        }
      }

      const searchParams = {
        page: page - 1,
        hitsPerPage: itemsPerPage,
        // Define searchable attributes with weights
        // Note: searchableAttributes should be configured in the Algolia dashboard 
        // and not passed with each query for optimal performance
        attributesToRetrieve: [
          "id",
          "logo",
          "businessName",
          "categories",
          "contractorId",
          "email",
          "generalInfo",
          "address",
          "phone",
          "rating",
          "reviewCount",
          "searches",
          "website",
          "bzScore",
        ],
        facets: ["*"],
        // Use explicit type for queryType
        queryType: "prefixAll" as "prefixAll" | "prefixLast" | "prefixNone", 
        // Use optionalWords to make search work like OR
        //optionalWords: searchQuery,
        // Alternatively, you can use removeWordsIfNoResults with proper type
        removeWordsIfNoResults: "lastWords" as "allOptional" | "none" | "lastWords" | "firstWords",
        // Apply any filters with AND logic between them
        filters: filterArray.length > 0 ? filterArray.join(" AND ") : undefined,
      };

      const { hits, nbHits } = await contractorsIndex.search<Contractor>(
        searchQuery,
        searchParams
      );

      return {
        contractors: hits as Contractor[],
        totalCount: nbHits,
      };
    } catch (error) {
      console.error("Error searching contractors:", error);
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
      throw error;
    }
  };

  return { searchContractors };
};